"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LifecycleCalculations = void 0;
const lodash_1 = __importDefault(require("lodash"));
const HighchartsThemeFacade_1 = require("src/hello_assets/src/components/ui/common/content/highcharts/HighchartsThemeFacade");
const DateTimeFacade_1 = require("src/hello_assets/src/util/DateTimeFacade");
const SeriesBuilder = {
    createAbstractSeriesLine: (data, name, color) => ({ type: "line", data, name, color }),
    createAbstractSeriesStacked: (data, name, stack, color) => ({ type: "column", data, name, stack, color }),
    createChurnedSeriesStacked: (data, legendName) => SeriesBuilder.createAbstractSeriesStacked(data, legendName, "negative", HighchartsThemeFacade_1.UsergeekHighchartsColors.red),
    createResurrectedSeriesStacked: (data, legendName) => SeriesBuilder.createAbstractSeriesStacked(data, legendName, "positive", HighchartsThemeFacade_1.UsergeekHighchartsColors.blue),
    createNewSeriesStacked: (data, legendName) => SeriesBuilder.createAbstractSeriesStacked(data, legendName, "positive", HighchartsThemeFacade_1.UsergeekHighchartsColors.green),
    createCurrentSeriesStacked: (data, legendName) => SeriesBuilder.createAbstractSeriesStacked(data, legendName, "positive", HighchartsThemeFacade_1.UsergeekHighchartsColors.lime),
    createChurnedSeriesLine: (data, legendName) => SeriesBuilder.createAbstractSeriesLine(data, legendName, HighchartsThemeFacade_1.UsergeekHighchartsColors.red),
    createResurrectedSeriesLine: (data, legendName) => SeriesBuilder.createAbstractSeriesLine(data, legendName, HighchartsThemeFacade_1.UsergeekHighchartsColors.blue),
    createNewSeriesLine: (data, legendName) => SeriesBuilder.createAbstractSeriesLine(data, legendName, HighchartsThemeFacade_1.UsergeekHighchartsColors.green),
    createCurrentSeriesLine: (data, legendName) => SeriesBuilder.createAbstractSeriesLine(data, legendName, HighchartsThemeFacade_1.UsergeekHighchartsColors.lime),
    createQuickRatioSeriesLine: (data, legendName) => ({ type: "line", data, name: legendName })
};
const calculateLifecycleSeriesSourceDataFromRawData = (rawData) => {
    const totalClients = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        return rawData.newUsers[index] + rawData.currentUsers[index] + rawData.resurrectedUsers[index];
    });
    const totalClientsForQuickRatio = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        return rawData.newUsers[index] + rawData.resurrectedUsers[index];
    });
    const newUsersPercent = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        const totalClient = totalClients[index];
        return totalClient > 0 ? (rawData.newUsers[index] / totalClient) * 100.0 : 0;
    });
    const currentUsersPercent = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        const totalClient = totalClients[index];
        return totalClient > 0 ? (rawData.currentUsers[index] / totalClient) * 100.0 : 0;
    });
    const resurrectedUsersPercent = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        const totalClient = totalClients[index];
        return totalClient > 0 ? (rawData.resurrectedUsers[index] / totalClient) * 100.0 : 0;
    });
    const churnedUsersPercent = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        const churnedCohort = rawData.churnedUsersCohort[index];
        return churnedCohort > 0 ? (rawData.churnedUsers[index] / churnedCohort) * 100.0 : 0;
    });
    const quickRatio = lodash_1.default.map(rawData.newUsers, (_v, index) => {
        const churned = rawData.churnedUsers[index];
        return churned > 0 ? (totalClientsForQuickRatio[index] / churned) : 0;
    });
    return {
        newUsersPercent: newUsersPercent,
        currentUsersPercent: currentUsersPercent,
        resurrectedUsersPercent: resurrectedUsersPercent,
        churnedUsersPercent: churnedUsersPercent,
        quickRatio: quickRatio,
        newUsers: rawData.newUsers,
        currentUsers: rawData.currentUsers,
        resurrectedUsers: rawData.resurrectedUsers,
        churnedUsers: rawData.churnedUsers,
        churnedUsersCohort: rawData.churnedUsersCohort
    };
};
const calculateClassicSeriesWithPercents = (resultData) => {
    const chartSeries = [];
    const legendResurrectedName = "Resurrected";
    const legendChurnedName = "Churned";
    const legendNewName = "New";
    const legendCurrentName = "Current";
    const seriesSourceData = calculateLifecycleSeriesSourceDataFromRawData(resultData.rawData);
    const initialDateMillis = resultData.intervalStartTimeSec * 1000;
    const initialMoment = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(initialDateMillis).startOf("day");
    const resurrectedData = [];
    const newData = [];
    const currentData = [];
    const churnedData = [];
    lodash_1.default.forEach(seriesSourceData.newUsersPercent, (_, index) => {
        const pointMillis = initialMoment.clone().add(index, "day").valueOf();
        const resurrectedValue = seriesSourceData.resurrectedUsersPercent[index];
        resurrectedData.push([pointMillis, resurrectedValue === 0 ? null : resurrectedValue]);
        const newValue = seriesSourceData.newUsersPercent[index];
        newData.push([pointMillis, newValue === 0 ? null : newValue]);
        const currentValue = seriesSourceData.currentUsersPercent[index];
        currentData.push([pointMillis, currentValue === 0 ? null : currentValue]);
        const churnedValue = seriesSourceData.churnedUsersPercent[index];
        churnedData.push([pointMillis, churnedValue === 0 ? null : -churnedValue]);
    });
    chartSeries.push(SeriesBuilder.createResurrectedSeriesStacked(resurrectedData, legendResurrectedName));
    chartSeries.push(SeriesBuilder.createNewSeriesStacked(newData, legendNewName));
    chartSeries.push(SeriesBuilder.createCurrentSeriesStacked(currentData, legendCurrentName));
    chartSeries.push(SeriesBuilder.createChurnedSeriesStacked(churnedData, legendChurnedName));
    return chartSeries;
};
const calculateClassicSeriesWithCounters = (resultData) => {
    const chartSeries = [];
    const legendResurrectedName = "Resurrected";
    const legendChurnedName = "Churned";
    const legendNewName = "New";
    const legendCurrentName = "Current";
    const seriesSourceData = calculateLifecycleSeriesSourceDataFromRawData(resultData.rawData);
    const initialDateMillis = resultData.intervalStartTimeSec * 1000;
    const initialMoment = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(initialDateMillis).startOf("day");
    const resurrectedData = [];
    const newData = [];
    const currentData = [];
    const churnedData = [];
    lodash_1.default.forEach(seriesSourceData.newUsers, (_, index) => {
        const pointMillis = initialMoment.clone().add(index, "day").valueOf();
        const resurrectedValue = seriesSourceData.resurrectedUsers[index];
        resurrectedData.push([pointMillis, resurrectedValue === 0 ? null : resurrectedValue]);
        const newValue = seriesSourceData.newUsers[index];
        newData.push([pointMillis, newValue === 0 ? null : newValue]);
        const currentValue = seriesSourceData.currentUsers[index];
        currentData.push([pointMillis, currentValue === 0 ? null : currentValue]);
        const churnedValue = seriesSourceData.churnedUsers[index];
        churnedData.push([pointMillis, churnedValue === 0 ? null : -churnedValue]);
    });
    chartSeries.push(SeriesBuilder.createResurrectedSeriesStacked(resurrectedData, legendResurrectedName));
    chartSeries.push(SeriesBuilder.createNewSeriesStacked(newData, legendNewName));
    chartSeries.push(SeriesBuilder.createCurrentSeriesStacked(currentData, legendCurrentName));
    chartSeries.push(SeriesBuilder.createChurnedSeriesStacked(churnedData, legendChurnedName));
    return chartSeries;
};
const calculateOvertimeSeries = (resultData) => {
    const chartSeries = [];
    const legendResurrectedName = "Resurrected";
    const legendChurnedName = "Churned";
    const legendNewName = "New";
    const legendCurrentName = "Current";
    const seriesSourceData = calculateLifecycleSeriesSourceDataFromRawData(resultData.rawData);
    const initialDateMillis = resultData.intervalStartTimeSec * 1000;
    const initialMoment = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(initialDateMillis).startOf("day");
    const resurrectedData = [];
    const newData = [];
    const currentData = [];
    const churnedData = [];
    lodash_1.default.forEach(seriesSourceData.newUsersPercent, (_, index) => {
        const pointMillis = initialMoment.clone().add(index, "day").valueOf();
        const resurrectedValue = seriesSourceData.resurrectedUsersPercent[index];
        resurrectedData.push([pointMillis, resurrectedValue]);
        const newValue = seriesSourceData.newUsersPercent[index];
        newData.push([pointMillis, newValue]);
        const currentValue = seriesSourceData.currentUsersPercent[index];
        currentData.push([pointMillis, currentValue]);
        const churnedValue = seriesSourceData.churnedUsersPercent[index];
        churnedData.push([pointMillis, -churnedValue]);
    });
    chartSeries.push(SeriesBuilder.createResurrectedSeriesLine(resurrectedData, legendResurrectedName));
    chartSeries.push(SeriesBuilder.createNewSeriesLine(newData, legendNewName));
    chartSeries.push(SeriesBuilder.createCurrentSeriesLine(currentData, legendCurrentName));
    chartSeries.push(SeriesBuilder.createChurnedSeriesLine(churnedData, legendChurnedName));
    return chartSeries;
};
const calculateQuickRatioSeries = (resultData) => {
    const chartSeries = [];
    const legendQuickRatio = "Quick Ratio";
    const seriesSourceData = calculateLifecycleSeriesSourceDataFromRawData(resultData.rawData);
    const initialDateMillis = resultData.intervalStartTimeSec * 1000;
    const initialMoment = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(initialDateMillis).startOf("day");
    const quickRatioData = [];
    lodash_1.default.forEach(seriesSourceData.quickRatio, (value, index) => {
        const pointMillis = initialMoment.clone().add(index, "day").valueOf();
        quickRatioData.push([pointMillis, value]);
    });
    chartSeries.push(SeriesBuilder.createQuickRatioSeriesLine(quickRatioData, legendQuickRatio));
    return chartSeries;
};
exports.LifecycleCalculations = {
    calculateClassicSeriesWithPercents: calculateClassicSeriesWithPercents,
    calculateClassicSeriesWithCounters: calculateClassicSeriesWithCounters,
    calculateOvertimeSeries: calculateOvertimeSeries,
    calculateQuickRatioSeries: calculateQuickRatioSeries,
};
